import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogFormButtons,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  type ImperativeDialogRef,
  useImperativeDialog,
} from "~/hooks/use-imperative-dialog";
import { useTRPC } from "~/lib/trpc";
import { isWebsiteUrl } from "~/utils/url-utils";

const formSchema = z.object({
  url: z
    .string()
    .min(1, { message: "URL is required" })
    .refine(isWebsiteUrl, { message: "URL is not valid" }),
});

type FormValues = z.infer<typeof formSchema>;

export function AddPageDialog({
  ref,
}: {
  ref: React.ForwardedRef<ImperativeDialogRef>;
}) {
  const { isOpen, setIsOpen } = useImperativeDialog(ref);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const createPage = useMutation(
    trpc.pages.create.mutationOptions({
      onError: () => null,
    }),
  );
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: "",
    },
  });

  const onSubmit = (data: FormValues) => {
    createPage.mutate(data, {
      onSuccess: async ({ page }) => {
        await queryClient.refetchQueries({
          queryKey: trpc.pages.getPageCrumbOptions.queryKey(),
        });
        toast.success("Page created");
        form.reset();
        setIsOpen(false);
        await navigate({
          to: "/pages/$pageId",
          params: { pageId: page.id },
        });
      },
      onError: (error) => {
        if (
          error.data?.code === "CONFLICT" ||
          error.data?.code === "NOT_FOUND" ||
          error.data?.code === "BAD_REQUEST"
        ) {
          form.setError("url", {
            type: "manual",
            message: error.message,
          });
        } else {
          toast.error(error.message);
        }
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add page</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <DialogForm onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Page URL</FormLabel>
                  <FormControl>
                    <Input placeholder="example.com/pricing" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogFormButtons
                isPending={createPage.isPending}
                onCancel={() => setIsOpen(false)}
              />
            </DialogFooter>
          </DialogForm>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
